/**
 * Kimlik doğrulama store — Zustand.
 * Access token in-memory — localStorage/sessionStorage YASAK.
 */
import { create } from 'zustand'
import { api, setAccessToken, setRefreshToken } from '../lib/api'
import { connectSocket, disconnectSocket } from '../lib/socket'
import { setLocale, SUPPORTED_LOCALES, type Locale } from '../i18n'

export interface AuthUser {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  /** Rolün etkin yetki anahtarları (dinamik RBAC) — /me döner */
  permissions?: string[]
  status: string
  officeId: string
  avatarUrl: string | null
  tenant: {
    id: string
    name: string
    subdomain: string
    plan: string
    status: string
    contractStartDate: string
    /** Firmanın varsayılan para birimi (/me döner) */
    defaultCurrency?: string
  }
}

export interface TenantOption {
  tenantId: string
  tenantName: string
  subdomain: string
  role: string
}

interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  isInitialized: boolean
  pendingTenantSelection: {
    selectionToken: string
    tenants: TenantOption[]
  } | null

  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: () => Promise<void>
  fetchMe: () => Promise<void>
  setUser: (user: AuthUser | null) => void
  selectTenant: (selectionToken: string, tenantId: string, rememberMe?: boolean) => Promise<void>
  clearTenantSelection: () => void
  switchTenant: (targetTenantId: string) => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: false,
  isInitialized: false,
  pendingTenantSelection: null,

  login: async (email, password, rememberMe = false) => {
    set({ isLoading: true })
    try {
      const res = await api.post<{
        data: {
          accessToken?: string
          refreshToken?: string
          rememberMe?: boolean
          requiresTenantSelection?: boolean
          selectionToken?: string
          tenants?: TenantOption[]
        }
      }>('/auth/login', { email, password, rememberMe })
      const data = res.data.data

      if (data.requiresTenantSelection) {
        set({
          pendingTenantSelection: {
            selectionToken: data.selectionToken!,
            tenants: data.tenants!,
          },
        })
        return
      }

      setAccessToken(data.accessToken!)
      setRefreshToken(data.refreshToken!, data.rememberMe ?? false)
      // /me ile tenant dahil tam kullanıcı bilgisini al
      let meRes
      try {
        meRes = await api.get<{ data: AuthUser }>('/auth/me')
      } catch (meErr) {
        // /me başarısız: token'ları temizle ve hatayı üst seviyeye fırlat
        setAccessToken(null)
        setRefreshToken(null)
        throw meErr
      }
      set({ user: meRes.data.data, isInitialized: true, pendingTenantSelection: null })
      connectSocket()
    } finally {
      set({ isLoading: false })
    }
  },

  selectTenant: async (selectionToken, tenantId, rememberMe = false) => {
    set({ isLoading: true })
    try {
      const res = await api.post<{
        data: { accessToken: string; refreshToken: string; rememberMe: boolean }
      }>('/auth/select-tenant', { selectionToken, tenantId, rememberMe })
      const { accessToken, refreshToken, rememberMe: persistent } = res.data.data
      setAccessToken(accessToken)
      setRefreshToken(refreshToken, persistent)
      const meRes = await api.get<{ data: AuthUser }>('/auth/me')
      set({ user: meRes.data.data, isInitialized: true, pendingTenantSelection: null })
      connectSocket()
    } finally {
      set({ isLoading: false })
    }
  },

  clearTenantSelection: () => set({ pendingTenantSelection: null }),

  switchTenant: async (targetTenantId) => {
    set({ isLoading: true })
    try {
      const res = await api.post<{
        data: { accessToken: string; refreshToken: string }
      }>('/auth/switch-tenant', { targetTenantId })
      const { accessToken, refreshToken } = res.data.data
      setAccessToken(accessToken)
      setRefreshToken(refreshToken, false)
      const meRes = await api.get<{ data: AuthUser }>('/auth/me')
      set({ user: meRes.data.data, isInitialized: true })
      connectSocket()
    } finally {
      set({ isLoading: false })
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout')
    } catch {
      // Token zaten geçersiz olabilir — devam et
    }
    setAccessToken(null)
    setRefreshToken(null)
    disconnectSocket()
    set({ user: null, pendingTenantSelection: null })
  },

  fetchMe: async () => {
    try {
      const res = await api.get<{ data: AuthUser & { language?: string } }>('/auth/me')
      const fetched = res.data.data
      set({ user: fetched, isInitialized: true })
      // Kullanıcının kayıtlı dilini uygula (varsa)
      if (fetched.language && (SUPPORTED_LOCALES as readonly string[]).includes(fetched.language)) {
        setLocale(fetched.language as Locale)
      }
      connectSocket()
    } catch {
      set({ user: null, isInitialized: true })
    }
  },

  setUser: (user) => {
    set({ user, isInitialized: true })
    if (user) connectSocket()
  },
}))

/**
 * Yetki kontrolü hook'u (dinamik RBAC).
 * `const can = useCan(); can('PROJECT_WRITE')` — kullanıcının rolünün etkin yetkisine bakar.
 * TENANT_ADMIN her zaman tam yetkilidir (güvenli varsayılan).
 */
export function useCan(): (permission: string) => boolean {
  const permissions = useAuthStore((s) => s.user?.permissions)
  return (permission: string): boolean => {
    // Yetki bilgisi henüz yoksa (eski payload / yükleniyor) kısıtlama uygulama.
    if (permissions === undefined) return true
    return permissions.includes(permission)
  }
}
